import {useTranslation} from '@/translations/i18n'
import {Text} from '@/components/ui/text/Text'
import {FileUploader} from '@/components/commons/file-upload/FileUpload'
import {AlertCircleIcon, Attachment02Icon} from '@/components/ui/icon/Icon'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useController, useFormContext} from 'react-hook-form'
import {AddPatientAbsenceFormValidationSchema} from '../../formSchemas'
import {useUploadDocument} from '@/services/useUploadDocument'
import {useDeleteDocument} from '@/services/useDeleteDocument'
import {toast} from '@/utilities/toast/toast'
import {useTheme} from 'styled-components'

export const CommunicatedAttachmentsField = () => {
    const {t} = useTranslation()
    const {control} = useFormContext<AddPatientAbsenceFormValidationSchema>()
    const controller = useController({control, name: 'communicatedAttachemnts'})

    const uploadDocument = useUploadDocument({
        onSuccess: response => {
            controller.field.onChange(response, {shouldDirty: true, shouldValidate: true})
            toast.success(t('commons:upload_success'))
        },
        onError: () => toast.error(t('commons:upload_error'))
    })
    const deleteDocument = useDeleteDocument({
        onSuccess: () => {
            controller.field.onChange(null, {shouldDirty: true, shouldValidate: true})
        }
    })

    const {palette} = useTheme()

    return (
        <Flexbox direction="column" gap={1.5} fullWidth>
            <Text palette="neutral" shade="950" size="textSm" weight="500">
                {t('agenda:communication_attachment')}
            </Text>
            <FileUploader
                document={controller.field.value}
                isLoading={uploadDocument.isPending || deleteDocument.isPending}
                inputFileProps={{
                    acceptedFiles: {'application/pdf': ['.pdf']},
                    maxSizeInMB: 8,
                    placeholder: t('projects:attach_file'),
                    onAcceptFiles: file => {
                        const formData = new FormData()
                        formData.append(`file`, file)
                        uploadDocument.mutate(formData)
                    },
                    typeIcon: <Attachment02Icon size={15} />,
                    hideButton: true,
                    width: '100%'
                }}
                onDeleteFile={deleteDocument.mutate}
                dropDownMenuProps={{
                    triggerWidth: '100%'
                }}
            />
            {controller.fieldState.error?.message && (
                <Flexbox gap={1} align="center">
                    <AlertCircleIcon size={15} color={palette.orange[500]} />
                    <Text palette="orange" shade="800" size="textSm" weight="400">
                        {t('agenda:communication_attachment_warning')}
                    </Text>
                </Flexbox>
            )}
        </Flexbox>
    )
}
